import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, TrendingUp, Users, Calendar, Award, Eye, Heart } from 'lucide-react';

const PortfolioSection = () => {
  const { language } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedProject, setSelectedProject] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const projects = [
    {
      id: 1,
      title: language === 'fr' ? 'Gala Annuel LUXE' : 'LUXE Annual Gala',
      category: language === 'fr' ? 'Événement Corporate' : 'Corporate Event',
      image: 'https://images.unsplash.com/photo-1761110787206-2cc164e4913c?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
      description: language === 'fr' 
        ? 'Organisation complète du gala annuel avec 500 invités VIP. Décoration somptueuse, entertainment live et expérience culinaire gastronomique.'
        : 'Complete organization of annual gala with 500 VIP guests. Sumptuous decoration, live entertainment and gourmet culinary experience.',
      stats: { guests: 500, satisfaction: 98, roi: '+340%' },
      year: '2024',
    },
    {
      id: 2,
      title: language === 'fr' ? 'Lancement Produit Tesla' : 'Tesla Product Launch',
      category: language === 'fr' ? 'Lancement de Marque' : 'Brand Launch',
      image: 'https://images.unsplash.com/photo-1771154140881-78acf32cdafe?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
      description: language === 'fr'
        ? 'Événement exclusif de lancement avec expérience immersive. Coverage médiatique internationale et engagement digital record.'
        : 'Exclusive launch event with immersive experience. International media coverage and record digital engagement.',
      stats: { guests: 300, satisfaction: 99, roi: '+520%' },
      year: '2024',
    },
    {
      id: 3,
      title: language === 'fr' ? 'Soirée Privée Selcius' : 'Selcius Private Evening',
      category: language === 'fr' ? 'Soirée Exclusive' : 'Exclusive Evening',
      image: 'https://images.unsplash.com/photo-1769812344099-8e52466aea8e?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
      description: language === 'fr'
        ? 'Création d\'une expérience gastronomique unique mêlant art culinaire et spectacle. Ambiance intimiste et raffinée.'
        : 'Creating a unique gastronomic experience blending culinary art and show. Intimate and refined atmosphere.',
      stats: { guests: 120, satisfaction: 100, roi: '+280%' },
      year: '2023',
    },
    {
      id: 4,
      title: language === 'fr' ? 'Festival BAO Summer' : 'BAO Summer Festival',
      category: language === 'fr' ? 'Festival' : 'Festival',
      image: 'https://images.unsplash.com/photo-1769812343590-485512e27838?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
      description: language === 'fr'
        ? 'Festival estival sur 3 jours avec concerts live, activités et expériences uniques. Plus de 5000 participants enthousiastes.'
        : '3-day summer festival with live concerts, activities and unique experiences. Over 5000 enthusiastic participants.',
      stats: { guests: 5000, satisfaction: 96, roi: '+450%' },
      year: '2024',
    },
    {
      id: 5,
      title: language === 'fr' ? 'Conférence Annuelle Tech' : 'Annual Tech Conference',
      category: language === 'fr' ? 'Conférence' : 'Conference',
      image: 'https://images.pexels.com/photos/14636315/pexels-photo-14636315.jpeg?auto=compress&cs=tinysrgb&w=800',
      description: language === 'fr'
        ? 'Organisation de la plus grande conférence tech de la région. Speakers internationaux et networking premium.'
        : 'Organization of the region\'s largest tech conference. International speakers and premium networking.',
      stats: { guests: 1200, satisfaction: 97, roi: '+380%' },
      year: '2023',
    },
    {
      id: 6,
      title: language === 'fr' ? 'Mariage de Rêve' : 'Dream Wedding',
      category: language === 'fr' ? 'Mariage' : 'Wedding',
      image: 'https://images.pexels.com/photos/35042249/pexels-photo-35042249.jpeg?auto=compress&cs=tinysrgb&w=800',
      description: language === 'fr'
        ? 'Mariage féerique dans un château provençal. Chaque détail soigneusement orchestré pour un jour inoubliable.'
        : 'Fairy tale wedding in a Provençal castle. Every detail carefully orchestrated for an unforgettable day.',
      stats: { guests: 200, satisfaction: 100, roi: 'N/A' },
      year: '2024',
    },
  ];

  const stats = [
    { 
      icon: Calendar, 
      value: '200+', 
      label: language === 'fr' ? 'Événements organisés' : 'Events organized',
      color: 'from-blue-500 to-cyan-500'
    },
    { 
      icon: Users, 
      value: '50K+', 
      label: language === 'fr' ? 'Invités satisfaits' : 'Satisfied guests',
      color: 'from-violet-500 to-purple-500'
    },
    { 
      icon: TrendingUp, 
      value: '+380%', 
      label: language === 'fr' ? 'ROI moyen client' : 'Average client ROI',
      color: 'from-amber-500 to-orange-500'
    },
    { 
      icon: Award, 
      value: '15', 
      label: language === 'fr' ? 'Prix remportés' : 'Awards won',
      color: 'from-emerald-500 to-teal-500'
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      }
    },
  };

  const buttonVariants = {
    initial: { scale: 1 },
    hover: { 
      scale: 1.05,
      boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10,
      }
    },
    tap: { scale: 0.95 },
  };

  return (
    <section
      id="portfolio"
      ref={ref}
      className="py-24 md:py-32 bg-neutral-50 overflow-hidden"
      data-testid="portfolio-section"
    >
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ type: "spring", stiffness: 200 }}
            className="inline-block text-xs font-semibold tracking-[0.3em] uppercase text-neutral-500 mb-4"
          >
            {language === 'fr' ? 'Nos Réalisations' : 'Our Work'}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ type: "spring", stiffness: 100, delay: 0.1 }}
            className="heading-xl text-4xl md:text-5xl lg:text-6xl mb-6 text-black"
            data-testid="portfolio-title"
          >
            {language === 'fr' ? 'Portfolio' : 'Portfolio'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="text-lg text-neutral-600 max-w-2xl mx-auto"
          >
            {language === 'fr' 
              ? 'Découvrez nos événements les plus mémorables et les résultats exceptionnels que nous avons obtenus pour nos clients.'
              : 'Discover our most memorable events and the exceptional results we achieved for our clients.'}
          </motion.p>
        </div>

        {/* Performance Stats */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16 md:mb-20"
        >
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ 
                  y: -8, 
                  scale: 1.02,
                  transition: { type: "spring", stiffness: 400 }
                }}
                className="relative bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-neutral-100 overflow-hidden group cursor-pointer"
                data-testid={`portfolio-stat-${index}`}
              >
                {/* Gradient background on hover */}
                <motion.div 
                  className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                />
                
                <motion.div
                  whileHover={{ rotate: 360, scale: 1.2 }}
                  transition={{ duration: 0.5 }}
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-4`}
                >
                  <IconComponent className="w-6 h-6 text-white" />
                </motion.div>
                <motion.div 
                  className="font-serif text-3xl md:text-4xl font-bold text-black mb-2"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.4 + index * 0.1, type: "spring" }}
                >
                  {stat.value}
                </motion.div>
                <div className="text-sm text-neutral-500">{stat.label}</div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
        >
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              variants={itemVariants}
              onHoverStart={() => setHoveredIndex(index)}
              onHoverEnd={() => setHoveredIndex(null)}
              whileHover={{ y: -12 }}
              onClick={() => setSelectedProject(project)}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg cursor-pointer"
              data-testid={`portfolio-project-${index}`}
            >
              {/* Image Container */}
              <div className="relative h-64 overflow-hidden">
                <motion.img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                  animate={{
                    scale: hoveredIndex === index ? 1.1 : 1,
                  }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />
                
                {/* Overlay */}
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"
                  animate={{
                    opacity: hoveredIndex === index ? 1 : 0.6,
                  }}
                  transition={{ duration: 0.3 }}
                />

                {/* Category Badge */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full text-xs font-semibold text-black"
                >
                  {project.category}
                </motion.div>

                {/* Year Badge */}
                <motion.div
                  className="absolute top-4 right-4 bg-black/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-white"
                >
                  {project.year}
                </motion.div>

                {/* Quick Stats on Hover */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ 
                    opacity: hoveredIndex === index ? 1 : 0,
                    y: hoveredIndex === index ? 0 : 20
                  }}
                  transition={{ duration: 0.3 }}
                  className="absolute bottom-4 left-4 right-4 flex justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5 text-white/90 text-sm">
                      <Users className="w-4 h-4" />
                      <span>{project.stats.guests}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-white/90 text-sm">
                      <Heart className="w-4 h-4" />
                      <span>{project.stats.satisfaction}%</span>
                    </div>
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 45 }}
                    className="w-10 h-10 bg-white rounded-full flex items-center justify-center"
                  >
                    <ArrowRight className="w-5 h-5 text-black" />
                  </motion.div>
                </motion.div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-serif text-xl font-semibold text-black mb-2 group-hover:text-neutral-700 transition-colors">
                  {project.title}
                </h3>
                <p className="text-sm text-neutral-500 line-clamp-2">
                  {project.description}
                </p>
                
                {/* ROI Badge */}
                {project.stats.roi !== 'N/A' && (
                  <motion.div 
                    className="mt-4 inline-flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-full text-sm font-medium"
                    whileHover={{ scale: 1.05 }}
                  >
                    <TrendingUp className="w-4 h-4" />
                    ROI: {project.stats.roi}
                  </motion.div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Button */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
        >
          <motion.a
            href="#contact"
            variants={buttonVariants}
            initial="initial"
            whileHover="hover"
            whileTap="tap"
            className="inline-flex items-center gap-3 btn-pill btn-primary text-lg px-10 py-5"
            data-testid="portfolio-cta"
          >
            {language === 'fr' ? 'Discuter de votre projet' : 'Discuss your project'}
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ArrowRight className="w-5 h-5" />
            </motion.div>
          </motion.a>
        </motion.div>
      </div>

      {/* Project Detail Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 50 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl"
            >
              <div className="relative h-64 md:h-80">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <button
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors"
                >
                  <span className="text-xl">&times;</span>
                </button>
                <div className="absolute bottom-6 left-6">
                  <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-black">
                    {selectedProject.category}
                  </span>
                </div>
              </div>
              
              <div className="p-8">
                <h3 className="font-serif text-2xl md:text-3xl font-bold text-black mb-4">
                  {selectedProject.title}
                </h3>
                <p className="text-neutral-600 leading-relaxed mb-6">
                  {selectedProject.description}
                </p>
                
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-neutral-50 rounded-xl p-4 text-center">
                    <div className="font-bold text-2xl text-black">{selectedProject.stats.guests}</div>
                    <div className="text-sm text-neutral-500">{language === 'fr' ? 'Invités' : 'Guests'}</div>
                  </div>
                  <div className="bg-neutral-50 rounded-xl p-4 text-center">
                    <div className="font-bold text-2xl text-black">{selectedProject.stats.satisfaction}%</div>
                    <div className="text-sm text-neutral-500">{language === 'fr' ? 'Satisfaction' : 'Satisfaction'}</div>
                  </div>
                  <div className="bg-neutral-50 rounded-xl p-4 text-center">
                    <div className="font-bold text-2xl text-green-600">{selectedProject.stats.roi}</div>
                    <div className="text-sm text-neutral-500">ROI</div>
                  </div>
                </div>

                <motion.a
                  href="#contact"
                  onClick={() => setSelectedProject(null)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="block w-full text-center btn-pill btn-primary py-4"
                >
                  {language === 'fr' ? 'Créer un événement similaire' : 'Create a similar event'}
                </motion.a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PortfolioSection;
